import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ViewChild } from '@angular/core';
import { LocaleSharedModule } from '@app/i18n/locale-shared.module';
import { LibraryElementsModule } from '@app/library/library-elements.module';
import { MeetingMiniCardComponent } from '@app/library/panes/meetings-drag-scroll/meeting-mini-card/meeting-mini-card.component';

import { MeetingsCardModule } from '@core/components/meeting-card/meetings-card.module';
import { MobileModalModule } from '@core/components/mobile-modal/mobile-modal.module';
import { ModalModule } from '@core/components/modal/modal.module';
import { PipesModule } from '@core/pipes/pipes.module';

import { IconComponent } from '@shared/icon/icon.component';
import { addDays, isSameDay, isToday, isTomorrow } from 'date-fns';
import { FormatPipeModule } from 'lingo2-ngx-date-fns';
import { AbstractPane } from '../models/abstract-pane';
import { FreeDateBannerComponent, TDisplayMode } from './free-date-banner/free-date-banner.component';
import type { Meeting } from 'lingo2-models';

@Component({
  selector: 'app-meetings-drag-scroll',
  standalone: true,
  imports: [
    LocaleSharedModule,
    CommonModule,
    LibraryElementsModule,
    PipesModule,

    FormatPipeModule,
    ScrollingModule,
    ModalModule,
    MeetingsCardModule,
    MobileModalModule,
    MeetingMiniCardComponent,
    FreeDateBannerComponent,
    IconComponent,
  ],
  templateUrl: './meetings-drag-scroll.component.html',
  styleUrls: ['./meetings-drag-scroll.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MeetingsDragScrollComponent extends AbstractPane {
  @ViewChild('scroller', { read: CdkVirtualScrollViewport }) scroller: CdkVirtualScrollViewport;

  public isVisible: boolean;
  public itemSize = 240;
  public filters: Date[];
  public indexFilterSelected = 0;
  public reachedPrev = false;
  public displayModeBanner: TDisplayMode = 'default';
  public reachedNext = false;
  public showModal = false;
  public openedCardIndex: number = null;
  public item: Meeting = null;
  /** Закомментировано в целях проверки для задачи: https://app.clickup.com/t/26mk7z6 */
  // public blockTitle: Partial<ILibraryTitle> = {
  //   title: 'library.titles.meetings',
  //   add_link: `/${EContentPanelRoute.classes}/create`,
  //   see_all_link: '/' + EContentPanelRoute.classes
  // };

  protected onContentLoaded(viaQuery?: boolean): void {
    this.blockTitle.title = this.title || 'library.titles.meetings';
    this.initFilter();
    this.isVisible = !!this._pane?.meetings?.length;
  }

  private initFilter() {
    this.filters = Array.from({ length: 7 }, (item, index) => addDays(new Date(), index));
  }

  public display(date: Date): any {
    // string | Date
    if (isToday(date)) {
      return 'library.meeting-drag-scroll.today';
    } else if (isTomorrow(date)) {
      return 'library.meeting-drag-scroll.tomorrow';
    }
    return date;
  }

  public selectFilter(index: number) {
    this.indexFilterSelected = index;
  }

  public meetingsFilter(): Meeting[] {
    const filtered = this.meetings.filter((meeting) =>
      isSameDay(new Date(meeting.begin_at), this.filters[this.indexFilterSelected]),
    );

    switch (filtered.length) {
      case 1:
        this.displayModeBanner = 'middle';
        break;
      case 2:
        this.displayModeBanner = 'short';
        break;
      default:
        this.displayModeBanner = 'default';
    }
    return filtered;
  }

  public trackByFn(index, item: Meeting): string {
    return item.id;
  }

  moveLeft() {
    this.scroller.scrollToIndex(0, 'smooth');
  }

  moveRight() {
    const offset = this.scroller.measureScrollOffset('left');
    // сдвинуть на 3 карточки
    this.scroller.scrollToOffset(offset + 3 * this.itemSize, 'smooth');
  }

  public onOpenCard(i) {
    // logger.debug('MeetingsDragScrollComponent:onOpenCard', i);
    this.openedCardIndex = i;
    this.item = this.meetingsFilter()[i];
    // if (this.baseUrl) {
    //   const url = this.router.createUrlTree(this.contentRoute(this.item)).toString();
    //   this.location.replaceState(url);
    // }
    this.checkReached();
    this.showModal = !!this.item;
  }

  public onCloseCard() {
    // logger.debug('ContentDragScrollComponent:onCloseCard');
    this.openedCardIndex = null;
    this.item = null;
    // if (this.baseUrl) {
    //   this.location.replaceState(this.baseUrl);
    // }
    // this.showPopover = false;
    this.showModal = false;
  }

  public onCloseModal(event) {
    if (event) {
      this.onCloseCard();
    }
  }

  public togglePrev(event?) {
    if (this.openedCardIndex - 1 >= 0) {
      this.onOpenCard(this.openedCardIndex - 1);
    }
    this.checkReached();
  }

  public toggleNext(event?) {
    if (this.openedCardIndex + 1 <= this.meetingsFilter().length - 1) {
      this.onOpenCard(this.openedCardIndex + 1);
    }
    this.checkReached();
  }

  public checkReached() {
    const reachedPrev = this.openedCardIndex <= 0;
    const reachedNext = this.openedCardIndex >= this.meetingsFilter().length - 1;

    if (!reachedPrev && !reachedNext) {
      this.reachedPrev = this.reachedNext = false;
    } else if (reachedPrev && reachedNext) {
      this.reachedPrev = this.reachedNext = true;
    } else if (reachedPrev) {
      this.reachedPrev = true;
    } else if (reachedNext) {
      this.reachedNext = true;
    }
  }
}
